"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { Search, ShieldCheck, Target } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/features/resume-jobfit-scorer";

// ============================================================================
// HERO TABS
// ============================================================================

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "match-score",
    name: "Match Score",
    title: "One Score for Every Job You Want",
    description:
      "Your score combines five weighted checks: keywords, evidence of impact, completeness, formatting and readability. You see the total and every part of it.",
    imageSrcUrl: `${IMG}/hero-match-score.png`,
    primaryActionText: "Score My Resume Free",
    primaryActionLink: "/resume/score",
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "missing-keywords",
    name: "Missing Keywords",
    title: "See What the Job Asks For",
    description:
      "Matched and missing keywords sit side by side, with how often the posting mentions each one. You know which gaps matter before you change a word.",
    imageSrcUrl: `${IMG}/hero-missing-keywords.png`,
    primaryActionText: "Score My Resume Free",
    primaryActionLink: "/resume/score",
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "fix-suggestions",
    name: "Fix Suggestions",
    title: "Specific Edits, Ranked by Impact",
    description:
      "Every recommendation points to a line in your resume and explains what it costs you. Work down the list, rescore, and watch the number move.",
    imageSrcUrl: `${IMG}/hero-fix-suggestions.png`,
    primaryActionText: "Score My Resume Free",
    primaryActionLink: "/resume/score",
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

// ============================================================================
// FEATURE GROUP 1 (dark)
// ============================================================================

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "two-modes",
    title: "Two Ways to Score",
    subtitle: "ATS MATCH OR PROFILE REVIEW",
    description:
      "Use ATS Match when you have a posting in hand; it scores your resume against that job. Use Profile Review for a general check of completeness, skills diversity and impact evidence.",
    benefits: [
      "ATS Match against any job description",
      "Profile Review without a posting",
      "Upload a PDF or paste plain text",
      "Every weight shown next to its score",
    ],
    primaryCTA: "Score My Resume",
    primaryLink: "/resume/score",
    secondaryCTA: "Build a Resume",
    secondaryLink: "/features/smart-resume-builder",
    imageSrcUrl: `${IMG}/two-modes.png`,
    icon: <Target className="w-4 h-4" />,
    stats: "2 analysis modes",
  },
  {
    id: "skills-gap",
    title: "Skills and Keyword Gap",
    subtitle: "FIND THE MISSING WORDS",
    description:
      "We pull the hard and soft skills from the posting and check each one against your resume. Matched, partly covered and missing skills are listed separately, so you fix the gaps that count first.",
    benefits: [
      "Hard and soft skills extracted",
      "Matched vs missing at a glance",
      "Keyword frequency from the posting",
      "A recommended next fix",
    ],
    primaryCTA: "Find My Gaps",
    primaryLink: "/resume/score",
    secondaryCTA: "See How It Works",
    secondaryLink: "/how-to-use",
    imageSrcUrl: `${IMG}/skills-gap.png`,
    icon: <Search className="w-4 h-4" />,
    stats: "Hard + soft skills",
    orientation: "reversed",
  },
];

// ============================================================================
// FEATURE GROUP 2 (light)
// ============================================================================

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "privacy",
    title: "Private by Design",
    subtitle: "PROCESSED IN MEMORY",
    description:
      "Your resume is analysed in memory and discarded as soon as your score is ready. Nothing is stored, so you can test as many drafts as you like.",
    benefits: [
      "Resume never saved to a server",
      "File discarded after scoring",
      "Works with PDF or pasted text",
      "Rescore every draft freely",
    ],
    primaryCTA: "Score My Resume",
    primaryLink: "/resume/score",
    imageSrcUrl: `${IMG}/privacy.png`,
    icon: <ShieldCheck className="w-4 h-4" />,
    stats: "Nothing stored",
  },
];

// ============================================================================
// TESTIMONIALS (replace with real user quotes before publishing)
// ============================================================================

const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "I was applying to roles where I matched 40% of the keywords and wondering why nobody replied. Now I only apply above 75, and my response rate has roughly tripled.",
    name: "Sneha P.",
    role: "UX Researcher",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "The scorer flagged that my two-column layout was breaking the parse. I switched to a single column, rescored, and had three calls the next week.",
    name: "Alexander",
    role: "Content Writer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "The recommendations are specific. It told me which bullet was missing a number and how many points that was costing me. Nobody had ever given me feedback that precise.",
    name: "Jalen Kyle",
    role: "Project Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop&q=80",
  },
];

// ============================================================================
// PAGE
// ============================================================================

export default function ResumeJobfitScorerPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Score Your Resume.",
            titleHighlight: "Beat the ATS.",
            subtitle:
              "Upload your resume, paste the job description and see your fit the way a screening system scores it. Get a match score, the keywords you are missing and the exact edits that close the gap.",
            ctaText: "Score My Resume Free",
            ctaLink: "/resume/score",
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Score. Fix. Apply.",
            sectionSubtitle:
              "Know where you stand before you apply, and what to change if you are not there yet.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Higher Scores, More Callbacks",
            subtitle:
              "From job seekers who fixed what screening software was rejecting",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About the JobFit Scorer",
            subtitle: "How the score works and how to use it.",
            faqs,
            ctaTitle: "Ready to see your score?",
            ctaText: "Score My Resume Free",
            ctaLink: "/resume/score",
          },
        },
      ]}
    />
  );
}
